import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { A, B, Bars, C, G, Num, Panel, R, k$, pTone, pct, usd } from "./HtuSlide";

const na = (v, f = (x) => x) => (v == null ? "n/a" : f(v));
const arrow = (v) => (v == null ? "" : v > 0 ? "▲" : v < 0 ? "▼" : "▬");
const Arr = ({ v }) => <b style={{ color: pTone(v) || "#94a3b8" }}>{arrow(v)} {na(v, pct)}</b>;

export const useOakton = () => {
  const [d, setD] = useState({});
  useEffect(() => {
    let on = true;
    const get = (p) => api.get(p).then((r) => r.data).catch(() => null);
    const load = () => Promise.all([get("/oakton/summary"), get("/oakton/outlook"), get("/oakton/deals")])
      .then(([sum, out, deals]) => on && setD({ sum, out, deals }));
    load(); const t = setInterval(load, 900000); return () => { on = false; clearInterval(t); };
  }, []);
  return d;
};

const Head = ({ tag, right }) => (
  <div className="flex items-baseline gap-[1em] min-w-0">
    <p className="nx-orbitron text-[2em] font-black tracking-wider text-white whitespace-nowrap" style={{ textShadow: `0 0 14px ${C}66` }}>Market Place on Oakton</p>
    <p className="fc-label text-[1em] text-cyan-300/80 whitespace-nowrap">Skokie, IL · {tag}</p>
    {right && <p className="ml-auto text-[1em] text-amber-200 truncate">{right}</p>}
  </div>
);
const Sync = ({ id }) => <div className="fc-panel p-[1em] fc-label" data-testid={id}>SYNCING MARKET PLACE ON OAKTON…</div>;

/* ---------------- slide 1: sales, payroll, departments ---------------- */
const Periods = ({ s }) => {
  const last = (k) => (s[k] || []).slice(-1)[0];
  const tiles = [["LATEST WEEK", s.latest ? { revenue: s.latest.revenue, chg_pct: s.latest.chg_pct, period: s.latest.label } : null],
    ["MONTH", last("monthly")], ["QUARTER", last("quarterly")], ["YEAR", last("yearly")]];
  return (
    <div className="grid grid-cols-4 gap-[0.35em]" data-testid="oak-periods">
      {tiles.map(([l, x]) => (
        <div key={l} className="rounded-[0.5em] border border-cyan-300/15 bg-cyan-300/[0.03] px-[0.6em] py-[0.3em] min-w-0" data-testid={`oak-period-${l.split(" ").pop().toLowerCase()}`}>
          <p className="fc-label truncate">{l} · {x?.period || "n/a"}{x?.weeks ? ` · ${x.weeks} wks` : ""}</p>
          <div className="flex items-baseline justify-between gap-[0.5em]">
            <p className="nx-orbitron text-[1.7em] font-black truncate text-[#E6F6FF]">{na(x?.revenue, usd)}</p>
            <p className="nx-orbitron text-[1.15em] whitespace-nowrap"><Arr v={x?.chg_pct} /></p>
          </div>
          <p className="fc-num text-[0.8em] text-cyan-200/60 truncate">{x?.avg_week ? `${usd(x.avg_week)} / week · vs prior period (weekly avg)` : "vs prior week"}</p>
        </div>
      ))}
    </div>
  );
};

const Payroll = ({ s }) => {
  const rows = (s.weekly || []).map((w) => ({ k: w.period, l: w.label.replace(" ", ""), v: w.payroll_pct, top: na(w.payroll_pct, (v) => `${v.toFixed(1)}%`), c: w.payroll_flag ? R : G, hi: w.payroll_flag }));
  const flags = (s.weekly || []).filter((w) => w.payroll_flag).length;
  const l = s.latest || {};
  return (
    <Panel title="PAYROLL AS % OF SALES · WEEKLY" code={flags ? `${flags} WEEKS ABOVE 15%` : "ALL WEEKS ≤ 15%"} testid="oak-payroll">
      <div className="flex gap-[0.6em] h-full">
        <div className="w-[9em] shrink-0 flex flex-col gap-[0.3em]">
          <Num l="LAST WK PAYROLL" v={na(l.payroll, usd)} sub={<span>{na(l.payroll_pct, (v) => `${v.toFixed(1)}% of sales`)}</span>} tone={l.payroll_flag ? R : G} testid="oak-payroll-latest" />
          <Num l="CUSTOMERS" v={na(l.customers, (v) => Math.round(v).toLocaleString())} sub={`avg ${na(l.avg_sale, (v) => `$${v.toFixed(2)}`)}`} testid="oak-customers" />
        </div>
        <div className="flex-1 min-w-0"><Bars data={rows} testid="oak-payroll-bars" /></div>
      </div>
    </Panel>
  );
};

const Dept = ({ d, i }) => (
  <p className="flex justify-between gap-[0.5em] text-[0.95em] border-b border-white/5 py-[0.12em]" data-testid={`oak-dept-${i}-${d.name.replace(/\W+/g, "-").toLowerCase()}`}>
    <span className="text-cyan-50 truncate">{d.name}</span>
    <span className="fc-num shrink-0">{k$(d.revenue)} · WoW <Arr v={d.wow_pct} /> · 4wk <Arr v={d.trend4_pct} /></span>
  </p>
);

export const OakSales = ({ d }) => {
  const s = d.sum;
  if (!s) return <Sync id="tv-scene-oak-sales" />;
  const weekly = (s.weekly || []).map((w) => ({ k: w.period, l: w.label.replace(" ", ""), v: w.revenue, top: w.chg_pct == null ? k$(w.revenue) : `${arrow(w.chg_pct)}${Math.abs(w.chg_pct).toFixed(1)}%`, c: w.chg_pct == null || w.chg_pct >= 0 ? B : R }));
  const share = (s.departments?.all || []).slice(0, 10).map((x) => ({ k: x.name, l: x.name.split(" ")[0].slice(0, 7), v: x.revenue, top: `${x.share_pct}%`, c: C }));
  return (
    <div className="h-full min-h-0 grid gap-[0.45em]" style={{ gridTemplateRows: "auto auto minmax(0,1fr) minmax(0,1fr) auto" }} data-testid="tv-scene-oak-sales">
      <Head tag="SALES · PAYROLL · DEPARTMENTS" right={`${s.weeks} weeks uploaded · updates Mondays`} />
      <Periods s={s} />
      <div className="min-h-0 grid gap-[0.45em]" style={{ gridTemplateColumns: "1.3fr 1fr" }}>
        <Panel title="WEEKLY REVENUE · WoW" code={`${(s.monthly || []).map((m) => `${m.period.slice(5)} ${arrow(m.chg_pct)}`).join("  ")}`} testid="oak-weekly"><Bars data={weekly} col={B} testid="oak-weekly-bars" /></Panel>
        <Payroll s={s} />
      </div>
      <div className="min-h-0 grid gap-[0.45em]" style={{ gridTemplateColumns: "1fr 1fr 1.2fr" }}>
        <Panel title="TOP 3 WINNERS · WoW" code="▲ GREEN" testid="oak-winners">{(s.departments?.winners || []).map((x, i) => <Dept key={x.name} d={x} i={`w${i}`} />)}</Panel>
        <Panel title="TOP 3 LOSERS · WoW" code="▼ RED" testid="oak-losers">{(s.departments?.losers || []).map((x, i) => <Dept key={x.name} d={x} i={`l${i}`} />)}</Panel>
        <Panel title="REVENUE SHARE · TOP 10 DEPARTMENTS" code="LATEST WEEK" testid="oak-share"><Bars data={share} col={C} testid="oak-share-bars" /></Panel>
      </div>
      <div className="fc-panel px-[0.8em] py-[0.4em] text-[1.05em] text-cyan-50 truncate" data-testid="oak-insight"><b className="text-amber-300 mr-[0.6em]">AI INSIGHT</b>{s.insight || "n/a"}</div>
    </div>
  );
};

/* ---------------- slide 2: Skokie growth, weather, weekly plan ---------------- */
const STAFF = { light: B, normal: G, heavy: A };
export const OakGrowth = ({ d }) => {
  const o = d.out;
  if (!o) return <Sync id="tv-scene-oak-growth" />;
  const dm = o.demographics || {};
  const wx = o.weather_correlation || {};
  return (
    <div className="h-full min-h-0 grid gap-[0.45em]" style={{ gridTemplateRows: "auto auto minmax(0,1fr) auto" }} data-testid="tv-scene-oak-growth">
      <Head tag="SKOKIE GROWTH · WEATHER · WEEK PLAN" right={`5-mile ZIPs ${(dm.zips || []).join(" ")}`} />
      <div className="grid grid-cols-8 gap-[0.35em]" data-testid="oak-demo">
        <Num l="POPULATION" v={na(dm.population, (v) => v.toLocaleString())} sub={dm.source || "n/a"} testid="oak-pop" />
        <Num l="MEDIAN INCOME" v={na(dm.median_income, usd)} sub="household-weighted" testid="oak-income" />
        <Num l="HOUSEHOLDS" v={na(dm.households, (v) => v.toLocaleString())} testid="oak-hh" />
        {Object.entries(dm.age_groups || {}).map(([k, v]) => <Num key={k} l={`AGE ${k}`} v={na(v.pct, (x) => `${x}%`)} sub={na(v.people, (x) => x.toLocaleString())} testid={`oak-age-${k}`} />)}
        {!dm.age_groups && <Num l="AGE GROUPS" v="n/a" testid="oak-age-na" />}
      </div>
      <div className="min-h-0 grid gap-[0.45em]" style={{ gridTemplateColumns: "1fr 1fr 1.25fr" }}>
        <Panel title="WHO SHOPS HERE · HERITAGE GROUPS" code="ACS ANCESTRY + ASIAN DETAIL" testid="oak-heritage">
          <div className="grid grid-cols-2 gap-x-[0.8em]">
            {Object.entries(dm.race_ethnicity || {}).map(([k, v]) => <p key={k} className="text-[0.9em] text-cyan-50 truncate">{k} <b className="text-cyan-200">{v.pct}%</b></p>)}
          </div>
          <div className="mt-[0.3em]">{(dm.top_heritage_groups || []).slice(0, 8).map((x) => (
            <div key={x.group} className="mb-[0.15em]"><p className="flex justify-between text-[0.88em]"><span className="text-cyan-50 truncate">{x.group}</span><span className="fc-num">{x.people.toLocaleString()} · {x.pct}%</span></p>
              <div className="h-[0.3em] rounded-full bg-white/10"><div className="h-full rounded-full" style={{ width: `${Math.min(100, x.pct * 8)}%`, background: C }} /></div></div>
          ))}</div>
        </Panel>
        <Panel title="OPPORTUNITY · COMPETITORS & GAPS" code={`${(o.competitors || []).length} NEARBY`} testid="oak-competitors">
          {(o.competitors || []).slice(0, 6).map((c, i) => <p key={i} className="text-[0.88em] text-cyan-50 truncate" data-testid={`oak-comp-${i}`}>▸ <b>{c.name}</b> <span className="text-cyan-200/60">{c.distance && c.distance !== "n/a" ? `· ${c.distance} ` : ""}— {c.note}</span></p>)}
          <p className="fc-label mt-[0.3em]">MARKET GAPS</p>
          {(o.gaps || []).map((g, i) => <p key={i} className="text-[0.88em] text-amber-100 leading-snug line-clamp-2" data-testid={`oak-gap-${i}`}>◆ {g}</p>)}
        </Panel>
        <Panel title="5 IMPROVEMENTS · FROM REAL NUMBERS" code="AI" testid="oak-improvements">
          {(o.improvements || []).map((x, i) => <div key={i} className="mb-[0.25em]" data-testid={`oak-imp-${i}`}><p className="text-[0.95em] font-black text-white truncate"><span className="text-[#2CF6B3] mr-[0.4em]">{i + 1}</span>{x.title}</p><p className="text-[0.85em] text-cyan-50/80 leading-snug line-clamp-2">{x.detail}</p></div>)}
        </Panel>
      </div>
      <div className="grid gap-[0.45em]" style={{ gridTemplateColumns: "0.9fr 2fr" }}>
        <Panel title="WEATHER vs SALES" code={`r(temp) ${na(wx.temp_sales_r)}`} testid="oak-weather">
          <div className="grid grid-cols-2 gap-x-[0.8em]">
            {(wx.impacts || []).map((x) => <p key={x.condition} className="flex justify-between text-[0.85em] gap-[0.4em]" data-testid={`oak-wx-${x.condition.split(" ")[0].toLowerCase()}`}><span className="truncate text-cyan-50">{x.condition}</span><span className="fc-num shrink-0">{x.weeks}w <Arr v={x.impact_pct} /></span></p>)}
          </div>
        </Panel>
        <Panel title="THIS WEEK'S PLAN · 7-DAY FORECAST" code="STAFFING · STOCK · PROMO" testid="oak-week-plan">
          <div className="grid grid-cols-7 gap-[0.3em]">
            {(o.week_plan || []).map((p) => (
              <div key={p.date} className="rounded-[0.5em] border border-cyan-300/15 px-[0.4em] py-[0.25em] min-w-0" data-testid={`oak-plan-${p.date}`}>
                <p className="fc-label">{new Date(`${p.date}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}</p>
                <p className="fc-num text-[0.9em] text-cyan-50">{Math.round(p.high_f)}° / {Math.round(p.low_f)}° · {p.rain_pct}%☂</p>
                <p className="text-[0.85em] font-black" style={{ color: STAFF[p.staffing] || "#94a3b8" }}>{(p.staffing || "n/a").toUpperCase()} STAFF</p>
                <p className="text-[0.78em] text-cyan-100/75 leading-tight line-clamp-2">{p.stock}</p>
                <p className="text-[0.78em] text-amber-200 leading-tight line-clamp-2">{p.promo}</p>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
};

/* ---------------- slide 3: live bulk deals from the Market Place app (rotates every visit) ---------------- */
const PER = 6;
let dealVisit = -1;
const TIER = [["FOOD", G], ["GROCERY", C], ["EQUIPMENT", A]];
const money = (v) => (v == null ? "n/a" : v < 100 ? `$${Number(v).toFixed(2)}` : usd(v));

const Mini = ({ l, v, c = "#e0f2fe" }) => (
  <div className="rounded bg-white/5 px-[0.5em] py-[0.3em]"><p className="fc-label text-[0.7em]">{l}</p><p className="fc-num text-[1.3em] font-bold" style={{ color: v === "n/a" ? "#64748b" : c }}>{v}</p></div>
);

const DealCard = ({ x, i }) => {
  const [tag, col] = TIER[x.tier] || TIER[2];
  const where = [x.city, x.state].filter(Boolean).join(", ") || x.address || "n/a";
  const contact = x.phone || x.email || (x.contact_via_listing ? "contact via listing" : "n/a");
  return (
    <div className="fc-panel p-[0.7em] min-h-0 flex flex-col gap-[0.3em] border-l-4" style={{ borderLeftColor: col }} data-testid={`oak-deal-${i}`}>
      <div className="flex items-center gap-[0.5em]">
        <span className="fc-label text-[0.8em] px-[0.5em] rounded whitespace-nowrap truncate max-w-[60%]" style={{ background: `${col}22`, color: col }}>{tag} · {x.category}</span>
        {x.hot && <span className="fc-label text-[0.8em] text-rose-300">HOT</span>}
        <span className="ml-auto fc-num text-[0.95em] text-cyan-200 whitespace-nowrap truncate">{x.distance_miles != null ? `${Math.round(x.distance_miles)} mi` : "n/a"} · {where}</span>
      </div>
      <p className="text-[1.6em] font-bold text-white leading-tight line-clamp-2" data-testid={`oak-deal-item-${i}`}>{x.item_name}</p>
      <p className="text-[1em] text-cyan-100/70 truncate">{[x.brand, x.pack_size, x.condition, x.quantity_text && `qty ${x.quantity_text} ${x.unit_label || ""}`].filter(Boolean).join(" · ")}</p>
      <div className="grid grid-cols-3 gap-[0.6em] my-auto" data-testid={`oak-deal-resale-${i}`}>
        <Mini l="RETAIL" v={money(x.typical_retail_per_unit ?? x.new_price)} />
        <Mini l="SELL AT" v={money(x.suggested_shelf_price)} c={C} />
        <Mini l="LOT PROFIT" v={money(x.total_profit)} c={G} />
      </div>
      <div className="flex items-end gap-[1em]">
        <div><p className="fc-label text-[0.7em]">LOT PRICE</p><p className="nx-orbitron text-[1.6em] font-black text-white" data-testid={`oak-deal-price-${i}`}>{money(x.total_lot_price)}</p></div>
        <div><p className="fc-label text-[0.7em]">PER UNIT</p><p className="fc-num text-[1.15em] text-cyan-50">{money(x.unit_price)}</p></div>
        {x.discount_pct != null && <div className="ml-auto text-right"><p className="fc-label text-[0.7em]">OFF {String(x.discount_basis).toUpperCase()}</p><p className="nx-orbitron text-[1.6em] font-black" style={{ color: G }}>{Math.floor(x.discount_pct)}%</p></div>}
      </div>
      <p className="text-[1.1em] text-amber-200 truncate border-t border-white/10 pt-[0.3em]" data-testid={`oak-deal-contact-${i}`}>
        <b className="text-white">{x.seller_name || x.source || "Seller"}</b>{x.contact_person ? ` (${x.contact_person})` : ""} · {contact}
      </p>
    </div>
  );
};

export const OakDeals = ({ d }) => {
  const [visit] = useState(() => ++dealVisit);
  const m = d.deals;
  if (!m) return <Sync id="tv-scene-oak-deals" />;
  const all = m.deals || [];
  const pages = Math.max(1, Math.ceil(all.length / PER));
  const page = visit % pages;
  const shown = all.slice(page * PER, page * PER + PER);
  return (
    <div className="h-full min-h-0 grid gap-[0.45em]" style={{ gridTemplateRows: "auto minmax(0,1fr)" }} data-testid="tv-scene-oak-deals">
      <Head tag="BULK DEALS · BUY BIG, SELL CHEAP" right={`${m.food || 0} food · ${m.grocery || 0} grocery · ${m.other || 0} other · nearest first`} />
      <Panel title="LIVE DEALS · FOOD & GROCERY FIRST" code={`PAGE ${page + 1}/${pages} · ${all.length} DEALS`} testid="oak-deals">
        {shown.length ? (
          <div className="grid grid-cols-3 grid-rows-2 gap-[0.6em] h-full min-h-0" data-testid="oak-deals-grid">
            {shown.map((x, i) => <DealCard key={x.deal_id || i} x={x} i={i} />)}
          </div>
        ) : <p className="fc-label text-[1.3em] text-amber-200 mt-[1em]" data-testid="oak-deals-empty">{(m.status || "no deals found, rechecking").toUpperCase()}</p>}
      </Panel>
    </div>
  );
};
